import { useEffect, useMemo, useRef, useState } from 'react';

import { useActiveWindowInterval } from 'components/hooks';
import { remainingDaysHoursMinutesSeconds } from 'lib/date';

/**
 * Hook responsible for countdown and repainting of all other child elements.
 */
export function useCountdownWithAutomaticRepaint({
  to,
  onComplete,
  disableLeftPad,
  disabled,
}: {
  to?: string | number;
  onComplete?: () => void;
  disableLeftPad?: boolean;
  disabled?: boolean;
}) {
  const [, setRefresher] = useState(0);
  const notifiedAboutComplete = useRef(false);

  const toDate = useMemo(() => (to ? new Date(to) : undefined), [to]);

  useEffect(() => {
    notifiedAboutComplete.current = false;
  }, [to]);

  useActiveWindowInterval(
    () => {
      if (disabled || notifiedAboutComplete.current) {
        return;
      }
      setRefresher(Date.now());
    },
    300,
    'ignore-missed'
  );

  const { ended, days, hours, minutes, seconds } =
    toDate === undefined
      ? { ended: '--', days: '--', hours: '--', minutes: '--', seconds: '--' }
      : remainingDaysHoursMinutesSeconds(toDate);

  useEffect(() => {
    if (ended && !notifiedAboutComplete.current && onComplete) {
      notifiedAboutComplete.current = true;
      onComplete();
    }
  }, [ended, notifiedAboutComplete, onComplete]);

  const addLeftPad = (value: string | number) =>
    disableLeftPad
      ? value
      : typeof value === 'number'
      ? value.toString().padStart(2, '0')
      : value;

  return {
    ended,
    days,
    daysNr: typeof days === 'number' ? days : undefined,
    hours: addLeftPad(hours),
    minutes: addLeftPad(minutes),
    seconds: addLeftPad(seconds),
  };
}

/**
 * Use callback countdown without actually rendering anyting
 *  - better performance as we only trigger React render on finish
 */
export function useCountdownCallback({
  to,
  onComplete,
  disabled,
}: {
  to?: string | number | Date | null;
  onComplete: () => void;
  disabled?: boolean;
}) {
  const [completed, setCompleted] = useState(false);
  const toDateTs = useMemo(
    () => (to instanceof Date ? to : to ? new Date(to) : undefined)?.getTime(),
    [to]
  );

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setCompleted(false);
  }, [toDateTs]);

  useActiveWindowInterval(
    () => {
      if (disabled || completed || !toDateTs) {
        return;
      }
      const ended = toDateTs <= Date.now();
      if (ended) {
        setCompleted(true);
        onComplete();
      }
    },
    completed || disabled ? 2000 : 300,
    'ignore-missed'
  );
}
